"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let posthogInstance: any = null;

async function initPostHog() {
  if (initialized || !POSTHOG_KEY || typeof window === "undefined") return;
  // Embedded previews (homepage/template-page iframes) must not record
  // sessions or capture events — same guard as Analytics.tsx.
  if (window.self !== window.top || window.location.search.includes("embed=true"))
    return;

  // Dynamic import — keeps posthog out of the initial bundle
  const { default: posthog } = await import("posthog-js");
  posthogInstance = posthog;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",

    // Session recording — the main reason we're using PostHog
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: ".rr-block",
    },

    // Autocapture — clicks, form submissions, pageviews
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,

    // Heatmaps
    enable_heatmaps: true,

    // Performance
    loaded: (ph) => {
      // In development, enable debug mode
      if (process.env.NODE_ENV === "development") {
        ph.debug();
      }
    },

    // Don't track dashboard pages
    before_send: (event) => {
      const url = event?.properties?.$current_url || "";
      if (url.includes("/dashboard")) return null;
      return event;
    },

    // Privacy
    respect_dnt: true,
    persistence: "localStorage+cookie",
  });

  initialized = true;
}

export default function PostHogProvider() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Initialize on mount — deferred via requestIdleCallback
  useEffect(() => {
    if (typeof requestIdleCallback !== "undefined") {
      requestIdleCallback(() => initPostHog(), { timeout: 3000 });
    } else {
      setTimeout(() => initPostHog(), 1000);
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (!initialized || !POSTHOG_KEY || !posthogInstance) return;
    if (pathname.startsWith("/dashboard")) return;

    // Capture pageview with full URL
    const url = window.origin + pathname + (searchParams.toString() ? `?${searchParams.toString()}` : "");
    posthogInstance.capture("$pageview", {
      $current_url: url,
    });
  }, [pathname, searchParams]);

  return null;
}
